import { LogoFull } from '../nav/LogoFull';
import { EngineeringConstellation } from './EngineeringConstellation';
import { HeroBackdrop } from './HeroBackdrop';
import './LandingPage.css';

const audiences = [
  {
    title: 'Developers',
    body: 'Find services, APIs, and documentation, then follow the path from repository to deployment.',
  },
  {
    title: 'Service owners',
    body: 'Understand who owns each service and how it connects to the rest of Snap.',
  },
  {
    title: 'Platform teams',
    body: 'Navigate infrastructure, deployments, and operations from a shared engineering catalog.',
  },
];

type LandingPageProps = { onEnter: () => void; busy?: boolean; error?: string };

export function LandingPage({
  onEnter,
  busy = false,
  error,
}: LandingPageProps) {
  return (
    <div className="snap-landing">
      <a className="snap-landing__skip" href="#snap-landing-main">
        Skip to content
      </a>
      <header className="snap-landing__header">
        <a
          className="snap-landing__brand"
          href="/"
          aria-label="Snap eHealth home"
        >
          <LogoFull color="#173448" size={14} />
        </a>
        <span className="snap-landing__breadcrumb">
          Snap eHealth / Backstage IDP
        </span>
      </header>
      <main id="snap-landing-main" tabIndex={-1}>
        <section
          className="snap-landing__hero"
          aria-labelledby="snap-landing-title"
        >
          <HeroBackdrop />
          <div className="snap-landing__copy">
            <p className="snap-landing__eyebrow">INTERNAL DEVELOPER PLATFORM</p>
            <h1 id="snap-landing-title">
              Backstage IDP<span>.</span>
            </h1>
            <p className="snap-landing__headline">
              Everything engineering, in one place.
            </p>
            <p className="snap-landing__body">
              Discover services, understand ownership, navigate infrastructure,
              and get from repository to production without hunting across tools
              and tribal knowledge.
            </p>
            <div className="snap-landing__entry">
              <button
                type="button"
                className="snap-landing__cta"
                onClick={onEnter}
                disabled={busy}
                aria-busy={busy}
                aria-describedby={error ? 'snap-sign-in-error' : undefined}
              >
                Enter Backstage IDP
              </button>
              <span className="snap-landing__status" role="status">
                {busy ? 'Connecting to GitHub…' : ''}
              </span>
            </div>
            {error && (
              <p
                id="snap-sign-in-error"
                className="snap-landing__error"
                role="alert"
              >
                {error}
              </p>
            )}
            <p className="snap-landing__support">
              One catalog. Clear ownership. Connected engineering systems.
            </p>
          </div>
          <EngineeringConstellation />
        </section>
        <section
          className="snap-landing__connected"
          aria-labelledby="snap-connected-heading"
        >
          <div>
            <p className="snap-landing__eyebrow">CONNECTED ENGINEERING</p>
            <h2 id="snap-connected-heading">
              One place to
              <br className="snap-landing__heading-break" /> understand how
              <br className="snap-landing__heading-break" /> Snap is built.
            </h2>
            <p className="snap-landing__intro">
              Your front door to services, teams, and the systems that connect
              them.
            </p>
          </div>
          <dl className="snap-landing__audiences">
            {audiences.map(audience => (
              <div className="snap-landing__audience" key={audience.title}>
                <dt>{audience.title}</dt>
                <dd>{audience.body}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>
    </div>
  );
}
